import { useState } from "react";
import { Phone, Mail, MessageCircle, Send, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";

/**
 * "Contacto" — host-facing page with the team's phone numbers, email,
 * Facebook page and an in-app message form that posts to /api/contact.
 *
 * The form pre-fills the host's name/email if available so they can send
 * a quick message without having to retype.
 */

const PHONES = [
  { number: "936942709", label: "Linha principal" },
  { number: "936942629", label: "Suporte alternativo" },
];
const EMAIL = "luxstaymadeira@gmail.com";
const FACEBOOK_URL = "https://www.facebook.com/share/1Hv9SvfaUB/?mibextid=wwXIfr";

export default function ClientContact() {
  const { company } = useCompany();
  const { user } = useAuth() || {};
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (message.trim().length < 5) {
      toast.error("Escreva uma mensagem com pelo menos 5 caracteres.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        message: message.trim(),
      });
      setSent(true);
      setMessage("");
      toast.success("Mensagem enviada — vamos responder em breve.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-8" data-testid="client-contact-page">
      <header>
        <div className="eyebrow text-[#d4af37] flex items-center gap-2">
          <MessageCircle size={14} /> Contacto
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Fale connosco</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
          Preferimos resolver tudo dentro da app, mas se for urgente os nossos
          números estão sempre disponíveis. Respondemos por WhatsApp, email
          ou Facebook.
        </p>
      </header>

      {/* Direct contact channels */}
      <section className="grid sm:grid-cols-2 gap-4" data-testid="contact-channels">
        {PHONES.map((p) => (
          <a key={p.number}
             href={`tel:+351${p.number}`}
             className="lux-card p-5 flex items-center gap-4 hover:border-[#d4af37] transition-colors"
             data-testid={`contact-phone-${p.number}`}>
            <div className="w-12 h-12 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] shrink-0">
              <Phone size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="eyebrow text-[10px] mb-0.5">{p.label}</div>
              <div className="font-display text-2xl text-[#fdfbf7]">+351 {p.number}</div>
              <div className="text-xs text-[#a0b2af] mt-0.5">
                Toque para ligar · WhatsApp disponível
              </div>
            </div>
          </a>
        ))}

        <a href={`mailto:${EMAIL}`}
           className="lux-card p-5 flex items-center gap-4 hover:border-[#d4af37] transition-colors"
           data-testid="contact-email">
          <div className="w-12 h-12 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] shrink-0">
            <Mail size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="eyebrow text-[10px] mb-0.5">Email</div>
            <div className="text-base text-[#fdfbf7] break-all">{EMAIL}</div>
            <div className="text-xs text-[#a0b2af] mt-0.5">Resposta em até 24h</div>
          </div>
        </a>

        <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer"
           className="lux-card p-5 flex items-center gap-4 hover:border-[#d4af37] transition-colors"
           data-testid="contact-facebook">
          <div className="w-12 h-12 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] shrink-0">
            <ExternalLink size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="eyebrow text-[10px] mb-0.5">Facebook</div>
            <div className="text-base text-[#fdfbf7]">{company.legal_name}</div>
            <div className="text-xs text-[#a0b2af] mt-0.5">Mensagens & atualizações</div>
          </div>
        </a>
      </section>

      {/* In-app message form */}
      <section className="lux-card p-6 md:p-8" data-testid="contact-form-section">
        <div className="eyebrow text-[#d4af37] mb-2">Mensagem direta</div>
        <h2 className="font-display text-2xl mb-6">Envie-nos uma mensagem</h2>

        {sent && (
          <div className="border border-[#10b981]/40 bg-[#10b981]/5 text-[#10b981] text-sm p-3 mb-4"
               data-testid="contact-form-success">
            Mensagem registada — vamos contactá-lo em breve.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                     required minLength={2} maxLength={120}
                     className="input"
                     data-testid="contact-name" />
            </Field>
            <Field label="Email">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required maxLength={120}
                     className="input"
                     data-testid="contact-email-input" />
            </Field>
          </div>
          <Field label="Telefone (opcional)">
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                   maxLength={40} placeholder="+351 …"
                   className="input"
                   data-testid="contact-phone-input" />
          </Field>
          <Field label="Mensagem">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                      required minLength={5} maxLength={2000} rows={6}
                      placeholder="Como podemos ajudar?"
                      className="input resize-none"
                      data-testid="contact-message" />
            <div className="text-[10px] text-[#a0b2af] text-right mt-1">
              {message.length}/2000
            </div>
          </Field>

          <div className="flex justify-end pt-2">
            <button type="submit" disabled={busy}
                    className="btn-gold px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="contact-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {busy ? "A enviar…" : "Enviar mensagem"}
            </button>
          </div>
        </form>
      </section>

      <style>{`
        .input {
          width: 100%;
          background: #062c27;
          border: 1px solid #1a4640;
          color: #fdfbf7;
          padding: 0.6rem 0.85rem;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.15s;
        }
        .input:focus { border-color: #d4af37; }
        .input::placeholder { color: #1a4640; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs uppercase tracking-widest text-[#a0b2af]">{label}</span>
      {children}
    </label>
  );
}
